import { useState, useEffect } from 'react';
import { useSeller } from '@/components/layout/DashboardLayout';
import { useProfile } from '@/lib/profile-context';
import { sellersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ChevronRight, Package } from 'lucide-react';
import { PredictiveInsightsBanner } from '@/components/PredictiveInsightsBanner';

const METRICS_WITHOUT_ORDER_ID = new Set(['policy_warnings', 'inventory_stockout', 'title_stuffing']);

function signalHidesOrderIdColumn(signal) {
  if (!signal) return true;
  if (signal.id === 'scraped-policy-warning-signal') return true;
  return METRICS_WITHOUT_ORDER_ID.has(signal.metric_key);
}

function signalShowsSkuColumn(signal) {
  return signal?.metric_key === 'inventory_stockout';
}

function getSeverityBadge(severity) {
  const s = (severity || '').toLowerCase();
  if (s === 'critical') return 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30';
  if (s === 'warning') return 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30';
  if (s === 'high risk' || s === 'high') return 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30';
  if (s === 'medium risk' || s === 'medium') return 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30';
  if (severity === 'High Risk') return 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30';
  if (severity === 'Medium Risk') return 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30';
  return 'bg-[hsl(142,72%,45%)]/15 text-[hsl(142,72%,45%)] border-[hsl(142,72%,45%)]/30';
}

export default function SignalsPage() {
  const { hasSelection } = useProfile();
  const { currentSellerId, syncVersion, syncing } = useSeller();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [signalOrders, setSignalOrders] = useState([]);
  const [signalDetailCaption, setSignalDetailCaption] = useState('');
  const [predictiveInsights, setPredictiveInsights] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  useEffect(() => {
    if (!currentSellerId) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      try {
        const cat = categoryFilter === 'all' ? undefined : categoryFilter;
        const res = await sellersAPI.getSignals(currentSellerId, undefined, cat);
        setSignals(res.data);
      } catch (err) {
        console.error('Failed to load signals:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentSellerId, categoryFilter, syncVersion]);

  const handleSignalClick = async (signal) => {
    setSelectedSignal(signal);
    setSignalDetailCaption('');
    setPredictiveInsights([]);
    setOrdersLoading(true);
    try {
      const res = await sellersAPI.getSignalOrders(currentSellerId, signal.id);
      setSignalOrders(res.data.orders || []);
      setSignalDetailCaption(res.data.detail_caption || '');
      setPredictiveInsights(res.data.predictive_insights || []);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setSignalOrders([]);
      setSignalDetailCaption('');
      setPredictiveInsights([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  if (loading || (hasSelection && syncing)) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Signals</h1>
          <p className="text-sm text-muted-foreground">These signals are contributing to your enforcement risk score.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[220px] h-9 text-sm bg-muted/40 border-border/60" data-testid="signals-category-select">
              <SelectValue placeholder="Signal Category Selected" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="Order Performance">Order Performance</SelectItem>
              <SelectItem value="Account Health & Compliance">Account Health</SelectItem>
              <SelectItem value="Shipping & Fulfillment">Shipping</SelectItem>
              <SelectItem value="Catalog & Listing Integrity">Catalog</SelectItem>
              <SelectItem value="Customer Experience & Feedback">Customer Experience</SelectItem>
              <SelectItem value="Security & Access Monitoring">Security</SelectItem>
              <SelectItem value="FBA Inventory Management">FBA Inventory</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="bg-card border-border/80" data-testid="signals-table">
        <CardContent className="p-0">
          <div className="rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="text-xs font-semibold">Signal Name</TableHead>
                  <TableHead className="text-xs font-semibold">Severity</TableHead>
                  <TableHead className="text-xs font-semibold">Current Value</TableHead>
                  <TableHead className="text-xs font-semibold">Baseline</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Score Contribution</TableHead>
                  <TableHead className="text-xs font-semibold w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {signals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-12">No active signals</TableCell>
                  </TableRow>
                )}
                {signals.map((signal) => (
                  <TableRow
                    key={signal.id}
                    className="hover:bg-muted/20 cursor-pointer"
                    onClick={() => handleSignalClick(signal)}
                    data-testid={`signal-row-${signal.signal_name?.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <TableCell className="text-sm font-medium">{signal.signal_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn('text-[10px] px-2 py-0.5 border', getSeverityBadge(signal.severity))}>
                        {signal.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{signal.current_value}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{signal.baseline}</TableCell>
                    <TableCell className="text-sm font-medium tabular-nums text-right text-[hsl(0,84%,60%)]">+{signal.score_contribution}</TableCell>
                    <TableCell><ChevronRight className="w-4 h-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {signals.length > 0 && (
            <div className="flex items-center justify-end px-4 py-3 border-t border-border/40">
              <p className="text-xs text-muted-foreground">
                Page <span className="font-medium text-foreground mx-1">01</span> of {Math.ceil(signals.length / 10)} Results
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Signal Orders Dialog */}
      <Dialog open={!!selectedSignal} onOpenChange={() => setSelectedSignal(null)}>
        <DialogContent
          className="bg-card border-border/80 max-w-3xl max-h-[90vh] flex flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
          data-testid="signal-orders-dialog"
        >
          {selectedSignal && (
            <>
              <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2 pr-14 space-y-1.5">
                <DialogTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-[hsl(188,100%,55%)]" />
                  {selectedSignal.signal_name}
                </DialogTitle>
                <DialogDescription className="space-y-1.5">
                  <span className="block text-foreground/90">
                    Current: {selectedSignal.current_value} | Baseline: {selectedSignal.baseline}
                  </span>
                  {signalDetailCaption && (
                    <span className="block text-xs text-muted-foreground leading-relaxed">{signalDetailCaption}</span>
                  )}
                </DialogDescription>
              </DialogHeader>
              {predictiveInsights.length > 0 && (
                <div className="flex-shrink-0 px-6 pb-2">
                  <PredictiveInsightsBanner insights={predictiveInsights} compact />
                </div>
              )}
              <div className="flex-1 min-h-0 flex flex-col px-6 pb-6">
                {ordersLoading ? (
                  <div className="space-y-2 py-2">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}
                  </div>
                ) : signalOrders.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    No order-level rows for this signal. See the note above for why.
                  </p>
                ) : (
                  <div className="rounded-lg border border-border/60 max-h-[min(60vh,520px)] overflow-y-auto overscroll-contain">
                    <table className="w-full caption-bottom text-sm">
                      <TableHeader className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm shadow-[0_1px_0_0_hsl(var(--border))]">
                        <TableRow className="bg-muted/30 hover:bg-muted/30 border-b border-border/60">
                          {!signalHidesOrderIdColumn(selectedSignal) && (
                            <TableHead className="text-xs font-semibold">Order ID</TableHead>
                          )}
                          {signalShowsSkuColumn(selectedSignal) && (
                            <TableHead className="text-xs font-semibold">SKU</TableHead>
                          )}
                          <TableHead className="text-xs font-semibold">ASIN</TableHead>
                          <TableHead className="text-xs font-semibold">
                            {signalShowsSkuColumn(selectedSignal) ? 'Snapshot' : 'Date'}
                          </TableHead>
                          <TableHead className="text-xs font-semibold">Status / detail</TableHead>
                          <TableHead className="text-xs font-semibold text-right">Impact</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {signalOrders.map((order, idx) => (
                          <TableRow key={order.id || order.order_id || idx} className="hover:bg-muted/20">
                            {!signalHidesOrderIdColumn(selectedSignal) && (
                              <TableCell className="text-sm font-mono">{order.order_id ?? '—'}</TableCell>
                            )}
                            {signalShowsSkuColumn(selectedSignal) && (
                              <TableCell className="text-sm font-mono">{order.sku || '—'}</TableCell>
                            )}
                            <TableCell className="text-sm text-muted-foreground">{order.asin ?? '—'}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{order.date ? String(order.date).replace('T', ' ').slice(0, 19) : '—'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border border-border/60 max-w-[280px] whitespace-normal text-left h-auto">
                                {order.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm font-medium tabular-nums text-right text-[hsl(0,84%,60%)]">{order.impact ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
